import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useMutation, useQuery, useNavigate, useParams } from '@common/hooks';
import {
  AppContent,
  ErrorHandler,
  LoadingState,
  Modal,
  Icon,
} from '@common/components';
import { useSetBreadcrumbs } from 'works/legacy/lib/client-store';
import {
  BulkUpdateAssignmentListsFromTemplate,
  WorksListTemplatesWithAssignmentRelatedQuery,
} from 'works/graphql/works-list-template';
import { OnboardingListTemplateSummary } from 'works/components/onboarding-list-template/onboarding-list-template-summary';
import { Button, Col, Row, Typography } from 'antd';
import { Colors } from 'works/legacy/lib/style-helper';
import AssignmentsToUpdateList from './assignments-to-update-list';
import { BottomActionBar } from './bottom-action-bar';
import { createMessage } from 'works/legacy/lib/message-helper';
import { ORGANIZATION_ADMIN_MENU } from 'works/pages/organization/admin-settings/organization-admin-vertical-nav-menu';

const { Title } = Typography;
export function OnboardingListsTemplateItemsConfirmUpdatePage() {
  const { id: worksListTemplateId } = useParams();

  useSetBreadcrumbs([
    {
      name: 'Admin',
      path: '/works/admin',
    },
    {
      name: 'Templates',
      path: '/works/admin/templates',
    },
    {
      name: ORGANIZATION_ADMIN_MENU.TEMPLATES__COMPLIANCE.LABEL,
      path: ORGANIZATION_ADMIN_MENU.TEMPLATES__COMPLIANCE.ROUTE,
    },
    {
      name: 'Items',
      path: `${ORGANIZATION_ADMIN_MENU.TEMPLATES__COMPLIANCE.ROUTE}/${worksListTemplateId}/items`,
    },
    {
      name: 'Confirm Updates',
      path: `${ORGANIZATION_ADMIN_MENU.TEMPLATES__COMPLIANCE.ROUTE}/${worksListTemplateId}/items/confirm-updates`,
    },
  ]);

  const navigate = useNavigate();

  const { loading, error, data } = useQuery(
    WorksListTemplatesWithAssignmentRelatedQuery,
    {
      variables: { id: worksListTemplateId },
    }
  );

  const errorMessageToast = () =>
    createMessage('error', 'Failed to update assignments. Please try again.');

  const [updateAssignmentLists, { loading: mutationLoading }] = useMutation(
    BulkUpdateAssignmentListsFromTemplate,
    {
      onCompleted(data) {
        if (!data?.error) {
          createMessage('success', 'Assignments were updated successfully');
          navigate(
            `/works/admin/templates/compliance-list/${worksListTemplate.id}/items`
          );
        } else {
          errorMessageToast();
        }
      },
      onError: errorMessageToast,
    }
  );

  if (loading) {
    return <LoadingState />;
  }

  if (error) {
    return (
      <ErrorHandler
        error={error}
        log="OnboardingListsTemplateItemsConfirmUpdatePage"
      />
    );
  }

  const worksListTemplate = data?.worksListTemplate;

  const onConfirm = () =>
    updateAssignmentLists({
      variables: { id: worksListTemplate.id },
    });

  const openUpdateAgreeAssignmentsModal = () => {
    Modal.confirm({
      title: "You're about to update listed assignments",
      content:
        'Changes are made on the background and can take up to several minutes to update.',
      okText: 'Continue Update',
      onOk: onConfirm,
      className: 'compliance-bulk-update',
    });
  };

  const { itemsAdded, itemsInactivated } =
    worksListTemplate.changesSinceLastBulkUpdate;

  return (
    <AppContent>
      <Title level={2}>Confirm Updates</Title>

      <OnboardingListTemplateSummary
        worksListTemplate={worksListTemplate}
        bottomElements={
          <TemplateItemsChanges
            itemsAdded={itemsAdded}
            itemsInactivated={itemsInactivated}
          />
        }
      />

      {worksListTemplate.worksListsToBeBulkUpdated && (
        <AssignmentsToUpdateList
          worksListTemplateId={worksListTemplate.id}
          items={worksListTemplate.worksListsToBeBulkUpdated}
        />
      )}

      <BottomActionBar
        showActionButton
        confirmLoading={mutationLoading}
        cancelText="Back"
        actionText="Update Assignments"
        onAction={openUpdateAgreeAssignmentsModal}
        onCancel={() => navigate(-1)}
      />

      <style jsx>{`
        :global(.compliance-bulk-update .ant-modal-content) {
          width: 500px;
        }
        :global(.compliance-bulk-update .ant-modal-confirm-body) {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
          align-items: center;
        }
        :global(
          .compliance-bulk-update
            .ant-modal-confirm-body
            .ant-modal-confirm-title
        ) {
          font-weight: bold;
        }
      `}</style>
    </AppContent>
  );
}

OnboardingListsTemplateItemsConfirmUpdatePage.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      id: PropTypes.string,
    }),
  }),
};

function TemplateItemsChanges({ itemsAdded, itemsInactivated }) {
  const [visible, setVisible] = useState(false);

  function changeDropdown(visible) {
    setVisible(visible);
  }

  return (
    <>
      <Row>
        <Col span={12}>
          <Title className="title" level={4}>
            ({itemsAdded.length}) ITEMS ADDED
          </Title>
          <ul className="compliance-list-changed">
            {visible &&
              itemsAdded.map((item, index) => (
                <li
                  className="compliance-items-changed"
                  key={`inactivated-${index}`}
                >
                  {item}
                </li>
              ))}
          </ul>
        </Col>
        <Col span={12}>
          <Title className="title" level={4}>
            ({itemsInactivated.length}) ITEMS MARKED INACTIVE
          </Title>
          <ul className="compliance-list-changed">
            {visible &&
              itemsInactivated.map((item, index) => (
                <li
                  className="compliance-items-changed"
                  key={`inactivated-${index}`}
                >
                  {item}
                </li>
              ))}
          </ul>
        </Col>
      </Row>
      <DropDownButton visible={visible} changeDropdown={changeDropdown} />
      <style jsx>{`
        .title {
          margin-top: 16px;
          font-size: 12px;
          color: ${Colors.midnightDarkest};
        }

        .compliance-list-changed {
          font-size: 16px;
          padding: 0;
          padding-left: 1.5rem;
          font-weight: 400;
          color: ${Colors.midnightLighter};
        }
      `}</style>
    </>
  );
}

TemplateItemsChanges.propTypes = {
  itemsAdded: PropTypes.array.isRequired,
  itemsInactivated: PropTypes.array.isRequired,
};

function DropDownButton({ visible, changeDropdown }) {
  return (
    <>
      <Button
        className="dropdown-button"
        onClick={() => changeDropdown(!visible)}
      >
        <span className="dropdown-button-text">
          {visible ? 'Hide items' : 'Show items'}
        </span>
        <Icon
          icon={visible ? 'chevronUp' : 'chevronDown'}
          kind="solid"
          size="md"
        />
      </Button>
      <style jsx>{`
        :global(.dropdown-button) {
          margin: 1em 0;
        }
        .dropdown-button-text {
          margin-right: 8px;
        }
      `}</style>
    </>
  );
}

DropDownButton.propTypes = {
  visible: PropTypes.bool.isRequired,
  changeDropdown: PropTypes.func.isRequired,
};
